import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Select } from '@ngxs/store';
import { merge, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AnimationService } from 'src/app/engine/animation.service';
import { EngineService } from 'src/app/engine/engine.service';
import { TimerangeModel } from 'src/app/engine/models/animation/timerange.model';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { EngineState } from 'src/app/engine/state/engine.state';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';

@Component({
  selector: 'app-ticker-block-item',
  templateUrl: './ticker-block-item.component.html',
  styleUrls: ['./ticker-block-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TickerBlockItemComponent implements OnInit, OnDestroy {
  // ---------- Child Elements ----------
  @ViewChild('image', { static: true }) image: ElementRef<HTMLImageElement>;
  @ViewChild('imagePre', { static: false }) imagePre: ElementRef<HTMLImageElement>;
  @ViewChild('imagePost', { static: false }) imagePost: ElementRef<HTMLImageElement>;

  @ViewChild('imageContainer', { static: true }) imageContainer: ElementRef<HTMLImageElement>;

  // ---------- Observables ----------
  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  @Select(StoryChaptersState.getSelectedChapterId)
  selectedChapterId$: Observable<string>;

  // ---------- Class Variables ----------
  ngUnsubscribe = new Subject();
  timerange: TimerangeModel;

  // ---------- Inputs ----------
  @Input()
  label: string;

  @Input()
  imageUrl: string;

  @Input()
  loopImage = false;

  // ---------- Lifecycle Hooks ----------
  constructor(
    private renderer: Renderer2,
    private engineService: EngineService,
    private animationService: AnimationService,
  ) {}

  ngOnInit(): void {
    this.initChapterListener();
    this.initTimelineListener();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ---------- Listeners ----------
  initChapterListener() {
    this.selectedChapterId$
      .pipe(
        filter((chapterId) => chapterId != null),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((chapterId) => {
        this.timerange = this.animationService.getChapterLocalTimerange(chapterId);
      });
  }

  initTimelineListener() {
    // Listen to timeline ticks
    merge(
      this.engineService.getEvent(EventsEnum.TimelineScrubbed),
      this.engineService.getEvent(EventsEnum.TimelinePlaying),
    )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: any) => {
        const percent = this.getChapterPercent(event.time);
        this.moveImage(percent);
      });
  }

  moveImage(percent: number) {
    const rect = this.image.nativeElement.getBoundingClientRect();
    const width = rect.width;

    const x = -(width * (percent / 100));

    this.renderer.setStyle(this.imageContainer.nativeElement, 'transform', `translate(${x}px, 0)`);

    // Looping Image
    if (this.loopImage) {
      this.renderer.setStyle(this.imagePre.nativeElement, 'transform', `translate(${-width}px, 0)`);
      this.renderer.setStyle(this.imagePost.nativeElement, 'transform', `translate(${width}px, 0)`);
    }
  }

  private getChapterPercent(time: number): number {
    const { firstTime, lastTime } = this.timerange;
    const localTime = this.animationService.mapGlobalTimeToLocal(time).time;

    // keep time in range
    const normalizedTime = Math.max(firstTime, Math.min(localTime, lastTime));
    const scrubPercent = (normalizedTime / lastTime) * 100;

    return scrubPercent;
  }
}
